import {
  CHART_AXIS,
  CHART_HORIZONTAL_DIMENSIONS,
} from '../shared/chart-tokens';

import {
  FALLBACK_TICK_WIDTH,
  TICK_HEIGHT,
  getTickLabel,
  type TickPayload,
} from './bar-chart-tick-utils';

const ELLIPSIS = '…';

export interface BarChartYAxisTickProps {
  x?: number;
  y?: number;
  payload?: TickPayload;
  /** Total axis pixel width. Injected by Recharts; sizes the hover hit area. */
  width?: number;
  /**
   * Injected by Recharts (`recharts-cartesian-axis-tick-value`). Forwarded onto
   * the <text> so `YAxis width="auto"` can measure the label's rendered bbox.
   */
  className?: string;
}

/** Caps a label by character count so auto-width doesn't expand to accommodate one long label.*/
const truncateLabel = (label: string, maxChars: number): string =>
  label.length > maxChars
    ? `${label.slice(0, Math.max(maxChars - 1, 0)).trimEnd()}${ELLIPSIS}`
    : label;

export const BarChartYAxisTick = ({
  x = 0,
  y = 0,
  payload,
  width: axisWidth,
  className,
}: BarChartYAxisTickProps) => {
  const fullLabel = getTickLabel(payload);

  if (!fullLabel) return null;

  const { categoryLabelMaxChars } = CHART_HORIZONTAL_DIMENSIONS;
  const display = truncateLabel(fullLabel, categoryLabelMaxChars);
  const isTruncated = display !== fullLabel;
  const hoverTargetWidth = axisWidth ?? FALLBACK_TICK_WIDTH;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <text
        className={className}
        x={0}
        y={0}
        textAnchor="end"
        dominantBaseline="central"
        fill={CHART_AXIS.tickColor}
        fontFamily={CHART_AXIS.tickFontFamily}
        fontSize={CHART_AXIS.tickFontSize}
      >
        {display}
      </text>
      {/* Transparent hit area so the full label is reachable on hover; an SVG
          <title> on the thin glyphs alone is nearly impossible to land on. */}
      {isTruncated && (
        <rect
          x={-hoverTargetWidth}
          y={-TICK_HEIGHT / 2}
          width={hoverTargetWidth}
          height={TICK_HEIGHT}
          fill="transparent"
          pointerEvents="all"
        >
          <title>{fullLabel}</title>
        </rect>
      )}
    </g>
  );
};
